/**
 * Mirrors `supabase/functions/_shared/revenue_response_key.ts` for hotel-facing UI.
 */

export function normalizeCheckinDate(value: string | null | undefined): string {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toISOString().slice(0, 10);
}

/** Calendar nights between check-in and check-out; null when invalid or checkout not after check-in. */
export function nightsBetweenStayDates(
  checkin: string | null | undefined,
  checkout: string | null | undefined,
): number | null {
  const ci = normalizeCheckinDate(checkin);
  const co = normalizeCheckinDate(checkout);
  if (ci === '' || co === '') return null;
  const ma = ci.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const mb = co.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!ma || !mb) return null;
  const da = Date.UTC(Number(ma[1]), Number(ma[2]) - 1, Number(ma[3]));
  const db = Date.UTC(Number(mb[1]), Number(mb[2]) - 1, Number(mb[3]));
  const diff = Math.floor((db - da) / 86400000);
  if (diff <= 0) return null;
  return diff;
}

/** `periodLabel` is `YYYY-MM` (campaign reporting month). */
export function checkinMatchesCampaignPeriod(checkin: string, periodLabel: string | undefined): boolean {
  if (!periodLabel) return true;
  const ci = normalizeCheckinDate(checkin);
  const m = ci.match(/^(\d{4})-(\d{2})/);
  if (!m) return false;
  return `${m[1]}-${m[2]}` === periodLabel;
}

function shiftCalendarMonth(year: number, month1to12: number, delta: number): { y: number; m: number } {
  const idx = year * 12 + (month1to12 - 1) + delta;
  return { y: Math.floor(idx / 12), m: (idx % 12) + 1 };
}

function daysInMonth(year: number, month1to12: number): number {
  return new Date(year, month1to12, 0).getDate();
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** First and last calendar day of the campaign reporting month (`period_label` is `YYYY-MM`). */
export function campaignPeriodMonthBounds(periodLabel: string | undefined): { first: string; last: string } | null {
  if (!periodLabel) return null;
  const m = periodLabel.match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const lastDay = daysInMonth(y, mo);
  const first = `${m[1]}-${m[2]}-01`;
  const last = `${m[1]}-${m[2]}-${pad2(lastDay)}`;
  return { first, last };
}

/** First day of (campaign month −1) through last day of (campaign month +1). Mirrors edge `manualLineStayWindowBounds`. */
export function manualLineStayWindowBounds(
  periodLabel: string | undefined,
): { first: string; last: string } | null {
  if (!periodLabel) return null;
  const m = periodLabel.match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const start = shiftCalendarMonth(y, mo, -1);
  const end = shiftCalendarMonth(y, mo, 1);
  const lastDay = daysInMonth(end.y, end.m);
  return {
    first: `${start.y}-${pad2(start.m)}-01`,
    last: `${end.y}-${pad2(end.m)}-${pad2(lastDay)}`,
  };
}

export function addCalendarDays(isoYmd: string, days: number): string {
  const ma = isoYmd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!ma) return isoYmd;
  const d = new Date(Date.UTC(Number(ma[1]), Number(ma[2]) - 1, Number(ma[3])));
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Default check-in = first day of reporting month; check-out = following day. */
export function defaultManualLineStayDates(periodLabel: string | undefined): {
  checkin: string;
  checkout: string;
} | null {
  const b = campaignPeriodMonthBounds(periodLabel);
  if (!b) return null;
  return { checkin: b.first, checkout: addCalendarDays(b.first, 1) };
}

/** Both dates must fall inside the campaign ±1 calendar month window (inclusive). */
export function stayDatesWithinManualLineWindow(
  checkin: string,
  checkout: string,
  periodLabel: string | undefined,
): boolean {
  const w = manualLineStayWindowBounds(periodLabel);
  if (!w) return true;
  const ci = normalizeCheckinDate(checkin);
  const co = normalizeCheckinDate(checkout);
  if (!ci || !co) return false;
  return ci >= w.first && ci <= w.last && co >= w.first && co <= w.last;
}
